/**
 * Tesoreria › Banche, reconciliation (07/10/2026): each movement of a bank
 * account is matched to the invoices it collects or pays, marked as a bank
 * cost, or given its reason (salary, F24…). The proposals are rules on the
 * description of the bank and on the amounts; the matching of invoices is
 * always confirmed by a person.
 */
import { z } from 'zod';
import { fromCents, toCents } from './money.js';
import { employeeName, inForce, SALARY_PAYMENT_KINDS, salaryPeriodDays, salaryPeriodLabel, } from './people.js';
import { noInvoiceFields } from './no-invoice.js';
import { nameKey } from './tax-ids.js';
import { transferChoiceSchema } from './transfers.js';
import { idSchema, optionalText, requiredText } from './validation.js';
/** Why a movement is not an invoice (to refine with use, decision of 07/10/2026). */
export const RECONCILIATION_REASONS = [
    'stipendio',
    'tfr',
    'f24',
    'giroconto',
    'finanziamento',
    'prelievo',
    'rateazione',
    'storno_rata',
    'altro',
];
export const RECONCILIATION_REASON_LABELS = {
    stipendio: 'Stipendio',
    tfr: 'TFR (versamento al fondo pensione)',
    f24: 'F24 (imposte e contributi)',
    giroconto: 'Giroconto fra conti',
    finanziamento: 'Anticipo o finanziamento',
    prelievo: 'Prelievo',
    rateazione: 'Rata di una rateazione',
    storno_rata: 'Storno di una rata di mutuo',
    altro: 'Altro',
};
/**
 * The rest of a movement reconciled with invoices (1.84, decision of
 * 15/10/2026): what is not an invoice, with its amount and what it is,
 * written by hand («Liquidazione recesso quote socio»).
 */
export const movementRestSchema = z.object({
    amount: z
        .string()
        .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
        .transform((value) => fromCents(toCents(value)))
        .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
    note: requiredText(300),
});
export const reconcileSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('fatture'),
        /** Each invoice with the part of the movement that goes to it, as a positive amount. */
        allocations: z
            .array(z.object({
            invoiceId: idSchema,
            amount: z
                .string()
                .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
                .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
        }))
            .min(1)
            .max(50),
        /**
         * Intesa (1.63): what the bank kept for its advance on each invoice,
         * collected with the movement but without money on the account.
         */
        retained: z
            .array(z.object({
            invoiceId: idSchema,
            amount: z
                .string()
                .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
                .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
            advanceMovementId: idSchema.nullable(),
        }))
            .max(50)
            .optional(),
        /**
         * Abbuono (1.81): the invoices whose residual left by the movement, up to
         * the limit, is closed without money.
         */
        writeOffs: z.array(idSchema).max(50).optional(),
        /** The part that is not an invoice (1.84), written by hand. */
        rest: movementRestSchema.optional(),
    }),
    z.object({ kind: z.literal('spese') }),
    z.object({
        /** Salaries and advances of the employees (1.35): needs the module Persone. */
        kind: z.literal('stipendi'),
        lines: z
            .array(z.object({
            employeeId: idSchema,
            year: z.number().int().min(2000).max(2100),
            month: z.number().int().min(1).max(13),
            kind: z.enum(SALARY_PAYMENT_KINDS),
            amount: z
                .string()
                .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
                .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
        }))
            .min(1)
            .max(100),
    }),
    z.object({
        /** The TFR paid to the pension funds of the employees (1.68): needs the module Persone. */
        kind: z.literal('tfr'),
        lines: z
            .array(z.object({
            employeeId: idSchema,
            amount: z
                .string()
                .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
                .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
        }))
            .min(1)
            .max(100)
            .refine((lines) => new Set(lines.map((line) => line.employeeId)).size === lines.length, {
            message: 'Un dipendente compare due volte',
        }),
        note: optionalText(300),
    }),
    z.object({
        kind: z.literal('motivo'),
        reason: z.enum(RECONCILIATION_REASONS),
        note: optionalText(300),
        /** For a transfer or a withdrawal (3.13): the other account and its movement. */
        transfer: transferChoiceSchema.optional(),
        /** For the reversal of a loan instalment (3.16): the instalment taken back. */
        reversedMovementId: idSchema.optional(),
    }),
    z.object({
        /**
         * Instalments of the rateazioni (3.15): the movement pays them; what it pays
         * besides (the other taxes of an F24) goes in the rest, written by hand.
         */
        kind: z.literal('rateazione'),
        dueIds: z
            .array(idSchema)
            .min(1)
            .max(24)
            .refine((ids) => new Set(ids).size === ids.length, { message: 'Una rata compare due volte' }),
        rest: movementRestSchema.optional(),
        note: optionalText(300),
    }),
    z.object({
        /** A movement of a loan without its number (a partner's loan, 1.50): all capital. */
        kind: z.literal('mutuo'),
        loanId: idSchema,
        note: optionalText(300),
    }),
    z.object({
        /**
         * To a subject without a document yet (2.22): the whole movement, or the
         * amount given, is a payment or collection of the subject waiting for its
         * invoice. Kept as a reconciliation with invoices that has none yet.
         */
        kind: z.literal('soggetto'),
        subjectId: idSchema,
        amount: z
            .string()
            .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
            .transform((value) => fromCents(toCents(value)))
            .refine((value) => toCents(value) > 0, { message: 'Importo non valido' })
            .optional(),
        note: optionalText(300),
        ...noInvoiceFields,
    }),
]);
/**
 * More invoices for a movement already reconciled with invoices (1.76): the
 * part of it still free, without undoing the others.
 */
export const movementInvoicesSchema = z.object({
    allocations: z
        .array(z.object({
        invoiceId: idSchema,
        amount: z
            .string()
            .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
            .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
    }))
        .min(1)
        .max(50),
});
/** Part of a movement given to a subject without a document (2.22), on a movement reconciled. */
export const subjectPaymentSchema = z.object({
    subjectId: idSchema,
    amount: z
        .string()
        .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
        .transform((value) => fromCents(toCents(value)))
        .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
    note: optionalText(300),
    /** For a subject that does not invoice (3.14). */
    ...noInvoiceFields,
});
/** A payment without document used on the invoice that arrived (2.22). */
export const useSubjectPaymentSchema = z.object({
    invoiceId: idSchema,
    amount: z
        .string()
        .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
        .transform((value) => fromCents(toCents(value)))
        .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
});
/**
 * What to check in a movement matched to invoices, salaries or TFR (1.69,
 * invoices 1.76): the part of the movement given to nobody, and the months
 * paid more than their net. Null when there is nothing to check, or the lines
 * are not visible. For invoices what is given is their sum with its sign: a
 * credit note paid together with its invoice takes off from it.
 */
export function reconciliationCheck(movementAmount, reconciliation) {
    if (reconciliation.kind === 'fatture') {
        if (reconciliation.auto || !reconciliation.invoices)
            return null;
        // What is not an invoice, written by hand, counts as given (1.84); so does what waits
        // for its document on a subject (2.22), as far as it is not on invoices already.
        const assigned = Math.abs(reconciliation.invoices.reduce((sum, item) => sum + toCents(item.amount), 0)) +
            toCents(reconciliation.rest?.amount ?? '0') +
            (reconciliation.subjectPayments ?? []).reduce(
            // Without invoice (3.14) the whole payment is the cost.
            (sum, item) => sum + toCents(item.withoutInvoice ? item.amount : item.available), 0);
        const unassigned = Math.abs(toCents(movementAmount)) - assigned;
        if (unassigned === 0)
            return null;
        return { assigned: fromCents(assigned), unassigned: fromCents(unassigned), overpaid: [] };
    }
    if (reconciliation.kind === 'rateazione') {
        if (!reconciliation.instalments)
            return null;
        // The instalments and the rest written by hand (3.15).
        const assigned = reconciliation.instalments.reduce((sum, item) => sum + toCents(item.amount), 0) +
            toCents(reconciliation.rest?.amount ?? '0');
        const unassigned = Math.abs(toCents(movementAmount)) - assigned;
        if (unassigned === 0)
            return null;
        return { assigned: fromCents(assigned), unassigned: fromCents(unassigned), overpaid: [] };
    }
    const lines = reconciliation.kind === 'stipendi'
        ? reconciliation.salaries
        : reconciliation.kind === 'tfr'
            ? reconciliation.tfr
            : null;
    if (!lines)
        return null;
    const assigned = lines.reduce((sum, line) => sum + toCents(line.amount), 0);
    const unassigned = Math.abs(toCents(movementAmount)) - assigned;
    const overpaid = reconciliation.kind === 'stipendi'
        ? (reconciliation.salaries ?? [])
            .filter((line) => line.overpaid)
            .map((line) => `${line.name} · ${salaryPeriodLabel(line.year, line.month)}`)
        : [];
    if (unassigned === 0 && overpaid.length === 0)
        return null;
    return { assigned: fromCents(assigned), unassigned: fromCents(unassigned), overpaid };
}
/** Words of a name too common to tell two companies apart. */
const COMMON = new Set([
    'srl',
    'spa',
    'sas',
    'snc',
    'srls',
    'sapa',
    'societa',
    'soc',
    'coop',
    'cooperativa',
    'ditta',
    'studio',
    'gruppo',
    'group',
    'italia',
    'italy',
    'ltd',
    'limited',
    'gmbh',
    'inc',
    'sarl',
    'the',
    'and',
    'del',
    'della',
    'dei',
    'per',
    'con',
    // Shared by many professional firms: alone they recognise nobody (1.41).
    'associati',
    'associato',
    'associate',
    'associazione',
    'partner',
    'partners',
]);
/** The words of a name that identify it: four letters or more, not legal forms. */
function nameWords(name) {
    return nameKey(name)
        .replace(/[^a-z0-9 ]+/g, ' ')
        .split(' ')
        .filter((word) => word.length >= 4 && !COMMON.has(word));
}
/**
 * The forms an invoice number takes in a bank description: «2026/112»,
 * «112/2026», «9/E», and the bare «112», which counts only after a word that
 * announces a document («FT», «fattura», «parcella», «N.»…).
 */
function numberForms(number) {
    const clean = number.toUpperCase().replace(/\s+/g, '');
    const forms = [{ form: clean, bare: false }];
    // «FT 45/2026» is written «45/2026» by the bank: the number without its letters.
    const core = /\d+[/-]\w+/.exec(clean)?.[0];
    if (core && core !== clean)
        forms.push({ form: core, bare: false });
    const parts = (core ?? clean).split(/[/\-_.]/).filter(Boolean);
    if (parts.length === 2) {
        forms.push({ form: `${parts[1]}/${parts[0]}`, bare: false });
        // «20/2025» is often written «20/25».
        const year = parts.findIndex((part) => /^20\d{2}$/.test(part));
        const shortYear = year < 0 && /^\d{1,5}$/.test(parts[0]) && /^[2-3]\d$/.test(parts[1]);
        if (year >= 0) {
            const other = parts[1 - year];
            forms.push({ form: `${other}/${parts[year].slice(2)}`, bare: false });
            // «FT 7 26», «FT8 2026» (1.52): the year after a space, only after a word of an invoice.
            forms.push({ form: `${other} ${parts[year].slice(2)}`, bare: true });
            forms.push({ form: `${other} ${parts[year]}`, bare: true });
        }
        else if (shortYear) {
            // «7/26»: a number and a year of two digits.
            forms.push({ form: `${parts[0]} ${parts[1]}`, bare: true });
            forms.push({ form: `${parts[0]}/20${parts[1]}`, bare: false });
        }
        // The bare number: for «7/26» the number, never the year.
        const digits = shortYear
            ? /^\d{2,3}$/.test(parts[0])
                ? parts[0]
                : null
            : (parts.find((part) => /^\d{2,3}$/.test(part)) ?? null);
        if (digits)
            forms.push({ form: digits, bare: true });
    }
    else if (/^\d{1,6}$/.test(clean)) {
        forms[0].bare = true;
    }
    return forms;
}
const DOCUMENT_WORD = '(?:FT|FATT|FATTURA|FATTURE|FAT|PARCELLA|PARC|DOC|N|NR|NUM|RIF)\\.?\\s*';
/** A number of one digit counts only after a word that surely means an invoice (1.40). */
const INVOICE_WORD = '(?:FT|FATT|FATTURA|FATTURE|FAT|PARCELLA|PARC)\\.?\\s*';
/**
 * What the banks write in every transfer and is no invoice: Intesa's
 * «N. 1 TOT. INTERNI EUR 7.500,00 N. 0 TOT. BANCHE EUR 0,00» (08/10/2026).
 */
const BANK_BOILERPLATE = /\bN\.\s*\d+\s+TOT\.\s*(?:INTERNI|BANCHE)\s+EUR\s+[\d.,]+/g;
const preparedNumbers = new Map();
const preparedCents = new Map();
const preparedNames = new Map();
const PREPARED_LIMIT = 50_000;
function numberPatterns(number) {
    let patterns = preparedNumbers.get(number);
    if (!patterns) {
        patterns = numberForms(number).map(({ form, bare }) => {
            // A space of the form is any space of the text («FT 7  26»).
            const escaped = form.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&').replace(/ /g, '\\s+');
            const word = form.length === 1 ? INVOICE_WORD : DOCUMENT_WORD;
            const before = bare ? `(^|[^A-Z0-9])${word}` : '(^|[^A-Z0-9])';
            return {
                runs: form.split(/[^A-Z0-9]+/).filter(Boolean),
                // A number followed by «/25» or «-2026» has its own year: «20/25» is not the invoice «20».
                pattern: new RegExp(`${before}${escaped}(?![/.\\-]\\d)([^A-Z0-9]|$)`),
            };
        });
        if (preparedNumbers.size > PREPARED_LIMIT)
            preparedNumbers.clear();
        preparedNumbers.set(number, patterns);
    }
    return patterns;
}
/**
 * The runs of letters and digits of a text, and the digits that end a run
 * («FT45» gives «45»): an invoice number can be in the text only if all its
 * runs are among them, checked before the pattern.
 */
function textRuns(text) {
    const runs = new Set();
    for (const [run] of text.matchAll(/[A-Z0-9]+/g)) {
        runs.add(run);
        const digits = /\d+$/.exec(run)?.[0];
        if (digits && digits !== run)
            runs.add(digits);
    }
    return runs;
}
function containsNumber(text, runs, number) {
    return (numberPatterns(number).some((form) => form.runs.every((run) => runs.has(run)) && form.pattern.test(text)) || listCites(text, number));
}
/** «INV/35/34», «INV/9/2/1/3»: the invoices a customer pays, after «INV» and between slashes (12/10/2026). */
const INVOICE_LIST = /(?:^|[^A-Z0-9])INV((?:\/\d{1,6})+)(?!\d)/g;
const preparedLists = new Map();
function listCites(text, number) {
    if (!text.includes('INV/'))
        return false;
    // A number alone, or with its year («35/2025», «35-25»): the number counts.
    const core = /^0*(\d{1,6})(?:[/-](?:20)?\d{2})?$/.exec(number.trim())?.[1];
    if (!core)
        return false;
    let cited = preparedLists.get(text);
    if (!cited) {
        cited = new Set();
        for (const match of text.matchAll(INVOICE_LIST)) {
            for (const part of match[1].split('/')) {
                if (part)
                    cited.add(part.replace(/^0+(?=\d)/, ''));
            }
        }
        if (preparedLists.size > PREPARED_LIMIT)
            preparedLists.clear();
        preparedLists.set(text, cited);
    }
    return cited.has(core);
}
/** An amount in cents, remembered: the same residuals come back for every movement. */
function centsOf(value) {
    let cents = preparedCents.get(value);
    if (cents === undefined) {
        cents = toCents(value);
        if (preparedCents.size > PREPARED_LIMIT)
            preparedCents.clear();
        preparedCents.set(value, cents);
    }
    return cents;
}
function counterpartyWords(name) {
    let words = preparedNames.get(name);
    if (!words) {
        words = nameWords(name);
        if (preparedNames.size > PREPARED_LIMIT)
            preparedNames.clear();
        preparedNames.set(name, words);
    }
    return words;
}
const BANK_DATES = /(data\s+(contabile|inserimento|valuta|esecuzione|regolamento|disposizione|accredito|addebito)|(spedit|eseguit|inserit|accreditat|addebitat)[oa]\s+il|del\s+giorno)\s*:?\s*\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}/gi;
/** The dates written in a bank description, «20/07/26», «08/06/2026», «4.5.26», as ISO dates. */
export function citedDates(text) {
    const dates = [];
    // The dates the bank adds of its own («Data contabile: 30/09/2026», «Eseguita il…») are not the payer's.
    const written = text.replace(BANK_DATES, ' ');
    for (const match of written.matchAll(/(^|[^0-9])(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})(?![0-9])/g)) {
        const day = Number(match[2]);
        const month = Number(match[3]);
        const year = match[4].length === 2 ? 2000 + Number(match[4]) : Number(match[4]);
        const date = new Date(Date.UTC(year, month - 1, day));
        if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day)
            continue;
        dates.push(date.toISOString().slice(0, 10));
    }
    return dates;
}
/**
 * The reason a movement probably has, for the page to propose (07/10/2026):
 * a salary when the bank says «stipendio» or «emolumenti». Proposed only, a
 * person confirms it.
 */
export function suggestedReason(movement) {
    const text = `${movement.description} ${movement.counterparty ?? ''}`;
    // Money coming back: the bank reverses a loan instalment (3.16).
    if (/storno/i.test(text) && movement.amount && toCents(movement.amount) > 0) {
        return 'storno_rata';
    }
    if (/stipend|emolument|retribuz/i.test(text))
        return 'stipendio';
    // A transfer between our accounts or a withdrawal (3.13).
    if (/giro ?conto/i.test(text))
        return 'giroconto';
    if (/prelev|prelievo/i.test(text))
        return 'prelievo';
    return /(^|[^a-z])tfr([^a-z]|$)|fondo pension|previdenza complementare/i.test(text)
        ? 'tfr'
        : null;
}
/**
 * The open invoices a movement may collect or pay, best first: money in is
 * looked for among the invoices issued, money out among those received; the
 * self-billing are left out. Points for the number of the invoice in the
 * description, for the name of the counterparty, for an amount equal to what
 * is left or to a due date, for the date of the invoice written in it; an
 * invoice dated after the movement goes down and is never the best.
 */
export function reconciliationProposals(movement, invoices, dues = new Map()) {
    const cents = Math.abs(toCents(movement.amount));
    const direction = toCents(movement.amount) > 0 ? 'emessa' : 'ricevuta';
    const text = `${movement.description} ${movement.counterparty ?? ''}`
        .toUpperCase()
        .replace(BANK_BOILERPLATE, ' ');
    const runs = textRuns(text);
    const words = new Set(nameWords(text));
    const dates = new Set(citedDates(movement.description));
    const candidates = [];
    for (const invoice of invoices) {
        if (invoice.direction !== direction || invoice.state === 'saldata')
            continue;
        // The issued copy of a self-billing is not collected from anybody (3.17).
        if (invoice.selfBilling && invoice.direction === 'emessa')
            continue;
        const residual = Math.abs(centsOf(invoice.residual));
        if (residual === 0)
            continue;
        const reasons = [];
        let score = 0;
        if (containsNumber(text, runs, invoice.number)) {
            score += 50;
            reasons.push('numero nella descrizione');
        }
        // The name of the invoice or an alias of its subject (1.73): the best of them counts.
        let whole = false;
        let part = false;
        for (const each of [invoice.counterpartyName, ...(invoice.aliases ?? [])]) {
            const name = counterpartyWords(each);
            const shared = name.filter((word) => words.has(word));
            if (!name.length || !shared.length)
                continue;
            if (shared.length === name.length)
                whole = true;
            else
                part = true;
        }
        if (whole || part) {
            // The whole name, or only a part of it: only the whole name recognises the counterparty.
            score += whole ? 30 : 20;
            reasons.push(whole ? 'nome della controparte' : 'parte del nome della controparte');
        }
        if (residual === cents) {
            score += 30;
            reasons.push('importo uguale al residuo');
        }
        else if ((dues.get(invoice.id) ?? []).some((due) => Math.abs(centsOf(due)) === cents)) {
            score += 25;
            reasons.push('importo uguale a una scadenza');
        }
        // Nothing in common with the movement: not a proposal (most invoices, quickly).
        if (!reasons.length)
            continue;
        // The date confirms an invoice found by number or name; alone it proposes nothing.
        if (dates.has(invoice.date)) {
            score += 25;
            reasons.push('data della fattura nella descrizione');
        }
        // An invoice may come after the payment of its pro forma (1.41): within
        // 60 days it is a candidate like the others; later it goes down.
        const later = daysBetween(movement.bookingDate, invoice.date) > PROFORMA_DAYS;
        if (later)
            score -= 40;
        candidates.push({
            invoiceId: invoice.id,
            number: invoice.number,
            date: invoice.date,
            subjectId: invoice.subjectId,
            counterpartyName: invoice.counterpartyName,
            residual: invoice.residual,
            origin: invoice.origin ?? 'gestify',
            ...(invoice.kind ? { kind: invoice.kind } : {}),
            amount: fromCents(Math.min(residual, cents)),
            score,
            reasons,
            best: false,
            later,
        });
    }
    // The counterparty first (1.52): when the description names one, the invoices
    // of others found only by their number are not proposals.
    const named = (proposal) => proposal.reasons.some((reason) => reason.includes('nome della controparte'));
    const pool = candidates.some(named)
        ? candidates.filter((proposal) => named(proposal) || proposal.reasons.some((reason) => reason.startsWith('importo')))
        : candidates;
    // Several invoices of the counterparty that make the movement together (1.52).
    const together = pool.some((proposal) => named(proposal) && Math.abs(centsOf(proposal.residual)) === cents)
        ? null
        : combination(pool.filter((proposal) => named(proposal) && !proposal.later), cents);
    for (const proposal of together ?? []) {
        proposal.score += 30;
        proposal.reasons.push('insieme alle altre: la somma è uguale al movimento');
        proposal.amount = fromCents(Math.abs(centsOf(proposal.residual)));
    }
    const scored = pool
        .filter((proposal) => proposal.score >= 20 || (proposal.later && proposal.reasons.length))
        .sort((a, b) => Number(a.later) - Number(b.later) ||
        b.score - a.score ||
        b.date.localeCompare(a.date) ||
        a.number.localeCompare(b.number))
        .slice(0, 8);
    const [first, second] = scored;
    if (together) {
        // The invoices together are the proposal to choose, all of them.
        for (const proposal of scored)
            proposal.best = together.includes(proposal);
    }
    else if (first &&
        !first.later &&
        first.score >= 60 &&
        (!second || second.later || first.score - second.score >= 20))
        first.best = true;
    return scored.map(({ later: _later, ...proposal }) => proposal);
}
/**
 * Two to four invoices of the same counterparty whose residuals add up to the
 * movement exactly, the most recent first; null when none or more than one
 * group do (then a person chooses).
 */
function combination(invoices, cents) {
    const groups = new Map();
    for (const invoice of invoices) {
        const key = invoice.subjectId ?? invoice.counterpartyName;
        groups.set(key, [...(groups.get(key) ?? []), invoice]);
    }
    const found = [];
    for (const group of groups.values()) {
        const items = [...group].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 12);
        const values = items.map((item) => Math.abs(centsOf(item.residual)));
        const search = (start, chosen, total) => {
            if (found.length > 1)
                return;
            if (chosen.length >= 2 && total === cents) {
                found.push(chosen.map((index) => items[index]));
                return;
            }
            if (chosen.length === 4 || total >= cents)
                return;
            for (let index = start; index < items.length; index += 1) {
                search(index + 1, [...chosen, index], total + values[index]);
            }
        };
        search(0, [], 0);
    }
    return found.length === 1 ? found[0] : null;
}
const MONTH_WORDS = [
    'GENNAIO',
    'FEBBRAIO',
    'MARZO',
    'APRILE',
    'MAGGIO',
    'GIUGNO',
    'LUGLIO',
    'AGOSTO',
    'SETTEMBRE',
    'OTTOBRE',
    'NOVEMBRE',
    'DICEMBRE',
    'TREDICESIMA',
];
/**
 * The month a salary transfer says it pays («STIPENDIO MESE MARZO»,
 * «TREDICESIMA»): in the year of the transfer, or the year before when the
 * month comes after it (December paid in January).
 */
export function citedSalaryMonth(description, bookingDate) {
    const text = description.toUpperCase();
    const index = MONTH_WORDS.findIndex((word) => new RegExp(`(^|[^A-Z])${word}([^A-Z]|$)`).test(text));
    if (index < 0)
        return null;
    const month = index + 1;
    const year = Number(bookingDate.slice(0, 4));
    const bookingMonth = Number(bookingDate.slice(5, 7));
    // The tredicesima is the one of the year of the transfer, paid in advance
    // during the year; only in January the one of the year before (1.80).
    if (month === 13)
        return { year: bookingMonth === 1 ? year - 1 : year, month };
    return { year: month > bookingMonth ? year - 1 : year, month };
}
/** How long after a payment on pro forma its invoice may come (1.41). */
export const PROFORMA_DAYS = 60;
/** The days from one date to another, negative when the second comes first. */
function daysBetween(from, to) {
    const day = (date) => Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
    return Math.round((day(to) - day(from)) / 86_400_000);
}
/** The order of a month: the tredicesima after December. */
function periodKey(year, month) {
    return year * 100 + month;
}
/**
 * Who a salary transfer pays, and which month (08/10/2026): the employee whose
 * IBAN the bank writes in the description, or whose name it writes; the
 * oldest month with something left, up to the month of the transfer, as
 * balance; without one, an advance on the month of the transfer. A transfer
 * nobody is found in is taken as the cumulative one of the month: every
 * employee in force with something left, for what is left.
 */
export function salaryProposals(movement, employees) {
    if (toCents(movement.amount) >= 0)
        return { lines: [], source: null };
    const cents = Math.abs(toCents(movement.amount));
    const text = nameKey(`${movement.description} ${movement.counterparty ?? ''}`).toUpperCase();
    const compact = text.replace(/[^A-Z0-9]/g, '');
    const bookingYear = Number(movement.bookingDate.slice(0, 4));
    const bookingMonth = Number(movement.bookingDate.slice(5, 7));
    const limit = periodKey(bookingYear, bookingMonth === 12 ? 13 : bookingMonth);
    const cited = citedSalaryMonth(movement.description, movement.bookingDate);
    // «ACCONTO» or «ANTICIPO» (1.80): an advance on the month.
    const advance = /(^|[^A-Z])(ACCONT|ANTICIP)/.test(text);
    const oldestOpen = (employee) => [...employee.open]
        .filter((item) => periodKey(item.year, item.month) <= limit && toCents(item.residual) > 0)
        .sort((a, b) => periodKey(a.year, a.month) - periodKey(b.year, b.month))[0] ?? null;
    const line = (employee, available) => {
        // The month the bank writes wins; an advance when it says so.
        const period = cited ?? { year: bookingYear, month: bookingMonth };
        const open = employee.open.find((item) => item.year === period.year && item.month === period.month);
        // A cited month already paid gives way to the oldest one still open (1.67):
        // «RETRIBUZIONE DICEMBRE» paid in December is often the tredicesima.
        const paid = !advance && open !== undefined && toCents(open.residual) <= 0;
        if ((cited || advance) && !(paid && oldestOpen(employee))) {
            return {
                employeeId: employee.id,
                name: employeeName(employee),
                year: period.year,
                month: period.month,
                kind: advance ? 'acconto' : 'saldo',
                amount: fromCents(open && !advance && !paid
                    ? Math.min(toCents(open.residual), available)
                    : Math.min(available, cents)),
            };
        }
        const oldest = oldestOpen(employee);
        return oldest
            ? {
                employeeId: employee.id,
                name: employeeName(employee),
                year: oldest.year,
                month: oldest.month,
                kind: 'saldo',
                amount: fromCents(Math.min(toCents(oldest.residual), available)),
            }
            : {
                employeeId: employee.id,
                name: employeeName(employee),
                year: bookingYear,
                month: bookingMonth,
                kind: 'acconto',
                amount: fromCents(available),
            };
    };
    const byIban = employees.filter((employee) => employee.iban && compact.includes(employee.iban.replace(/\s+/g, '')));
    const byName = employees.filter((employee) => {
        const words = nameKey(`${employee.lastName} ${employee.firstName}`)
            .toUpperCase()
            .split(/[^A-Z0-9]+/)
            .filter((word) => word.length >= 2);
        return (words.length > 0 &&
            words.every((word) => new RegExp(`(^|[^A-Z0-9])${word}([^A-Z0-9]|$)`).test(text)));
    });
    const found = byIban.length ? byIban : byName;
    if (found.length) {
        return {
            lines: found.map((employee) => line(employee, cents)),
            source: byIban.length ? 'iban' : 'nome',
        };
    }
    const { from, to } = salaryPeriodDays(bookingYear, bookingMonth);
    const lines = employees
        .filter((employee) => inForce(employee, from, to) &&
        (cited
            ? employee.open.some((item) => item.year === cited.year &&
                item.month === cited.month &&
                toCents(item.residual) > 0)
            : oldestOpen(employee)))
        .map((employee) => line(employee, Number.MAX_SAFE_INTEGER));
    return { lines, source: lines.length ? 'mese' : null };
}
/**
 * Whether a text cites an invoice number, with the same rules as the bank
 * descriptions: used for the invoice a credit note corrects (1.58).
 */
export function textCitesNumber(text, number) {
    const upper = text.toUpperCase();
    return containsNumber(upper, textRuns(upper), number);
}
//# sourceMappingURL=reconciliation.js.map